/**
 * /livret-circulation — Livre interactif public (Circulation A1/A2)
 *
 * Version circulation du Livret CODE 2 ROUES.
 * 7 chapitres alignés sur le REMC (Référentiel Éducation Mobilité Citoyenne)
 * et l'épreuve circulation A2 (~40 min, coefficient 3).
 *
 * Chapitres :
 *   01 Bienvenue en circulation (sortir du plateau, mental)
 *   02 Prise en main avant départ (EPI, vérifs, réglages)
 *   03 Regard, allure, position (adaptation)
 *   04 Trajectoire de sécurité en virage
 *   05 Autres usagers (croisements, dépassements, priorités)
 *   06 Vérifications sécurité (question orale examen)
 *   07 Prêt pour l'examen ? (bilan et check-list)
 *
 * Persistance : localStorage (identique au livret plateau).
 * Design : identique au livret plateau (dark, nav sommaire, progression).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ChevronLeft, ChevronRight, Menu, X, Home,
  Eye, Feather, Scale, Repeat, CheckCircle2, Circle, AlertCircle,
  Sparkles, Bike, Route, Zap, Users, Shield, Target, Trophy,
  Wind, MapPin, Gauge, Car, Truck, Signpost, Download,
  Presentation, QrCode,
} from 'lucide-react';
import TrameLivretCross from '../../components/trames/TrameLivretCross';
import SaveProgressCTA from '../../components/livrets/SaveProgressCTA';
import LivretQrShareModal from '../../components/livrets/LivretQrShareModal';

const STORAGE_KEY = 'code2roues:livret-circulation-book:v1';

const CHAPTERS = [
  { id: 'cover',   n: '',   title: 'Couverture',                                icon: Bike,     color: '#0ea5e9' },
  { id: 'ch01',    n: '01', title: 'Bienvenue en circulation',                   icon: Sparkles, color: '#22c55e' },
  { id: 'ch02',    n: '02', title: 'Prise en main avant départ',                 icon: Shield,   color: '#eab308' },
  { id: 'ch03',    n: '03', title: 'Regard · Allure · Position',                 icon: Eye,      color: '#f97316' },
  { id: 'ch04',    n: '04', title: 'Trajectoire de sécurité en virage',          icon: Route,    color: '#06b6d4' },
  { id: 'ch05',    n: '05', title: 'Cohabiter avec les autres usagers',          icon: Users,    color: '#6366f1' },
  { id: 'ch06',    n: '06', title: 'Vérifications sécurité (question orale)',    icon: AlertCircle, color: '#a855f7' },
  { id: 'ch07',    n: '07', title: "Prêt pour l'examen ?",                       icon: Trophy,   color: '#ec4899' },
];

/**
 * Keys d'items cochables par chapitre — même logique que le livret Plateau.
 * Sert au calcul des badges de progression dans le sommaire et
 * à l'affichage automatique du CTA « Chapitre complet ».
 */
const CHAPTER_ITEM_KEYS = {
  cover: [],
  ch01: ['c-regard', 'c-douceur', 'c-equilibre', 'c-regularite'],
  ch02: ['c02-casque', 'c02-gants', 'c02-blouson', 'c02-pantalon', 'c02-bottes', 'c02-gilet', 'c02-huile', 'c02-liquide', 'c02-pneus', 'c02-lumieres', 'c02-chaine'],
  ch03: ['c03-regard-loin', 'c03-regard-mobile', 'c03-allure', 'c03-position-hors', 'c03-position-ville', 'c03-anticipation', 'c03-mains-libres'],
  ch04: ['c04-freine-avant', 'c04-retrograde', 'c04-place-large', 'c04-point-corde', 'c04-regard-sortie', 'c04-relance', 'c04-marge-median'],
  ch05: ['c05-croisement', 'c05-depassement', 'c05-camion', 'c05-angle-mort', 'c05-piste', 'c05-priorite-dr', 'c05-passage-p'],
  ch06: ['c06-freinage-avant', 'c06-freinage-arr', 'c06-abs', 'c06-eclairage', 'c06-vent', 'c06-pluie', 'c06-nuit'],
  ch07: ['c07-plateau-ok', 'c07-etm-ok', 'c07-h20-circu', 'c07-milieu-varies', 'c07-question-ok', 'c07-moniteur-go', 'c07-eqp-complet'],
};

function isItemDone(state, key) {
  const v = state[key];
  if (v === true || v === 'ok') return true;
  if (v === false || v === null || v === undefined) return false;
  if (typeof v === 'string') {
    if (v === '' || v === 'none') return false;
    return true;
  }
  return !!v;
}

function chapterProgress(state, chapterId) {
  const keys = CHAPTER_ITEM_KEYS[chapterId] || [];
  const total = keys.length;
  const done = keys.filter((k) => isItemDone(state, k)).length;
  return { done, total, complete: total > 0 && done === total };
}

function loadState() {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}'); }
  catch (_) { return {}; }
}
function saveState(state) { localStorage.setItem(STORAGE_KEY, JSON.stringify(state)); }

export default function LivretCirculationBookPublic() {
  // Deep-link : ?chapter=<id> ouvre directement le bon chapitre
  // (utilisé par les QR partagés en fin de séance ou par un moniteur).
  const initialChapterIdx = (() => {
    if (typeof window === 'undefined') return 0;
    const p = new URLSearchParams(window.location.search);
    const wanted = p.get('chapter');
    if (!wanted) return 0;
    const idx = CHAPTERS.findIndex((c) => c.id === wanted);
    return idx >= 0 ? idx : 0;
  })();
  const [chapterIdx, setChapterIdx] = useState(initialChapterIdx);
  const [state, setState] = useState({});
  const [menuOpen, setMenuOpen] = useState(false);
  const [qrOpen, setQrOpen] = useState(false);
  const [profil, setProfil] = useState({ eleve: '', moniteur: '', debut: '' });
  // Dismiss du CTA « Chapitre suivant » pour ne pas re-spammer l'élève
  const [dismissedNext, setDismissedNext] = useState({});

  useEffect(() => {
    document.title = 'Livret circulation A2 · Livre interactif · code2roues';
    const s = loadState();
    setState(s);
    if (s.__profil) setProfil(s.__profil);
  }, []);

  // Synchronise l'URL avec le chapitre courant + scroll top à chaque bascule.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const p = new URLSearchParams(window.location.search);
    const currentQ = p.get('chapter');
    const wantedQ = CHAPTERS[chapterIdx]?.id;
    if (currentQ !== wantedQ && wantedQ) {
      p.set('chapter', wantedQ);
      const newUrl = window.location.pathname + '?' + p.toString();
      window.history.replaceState(null, '', newUrl);
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [chapterIdx]);

  const setValue = (key, value) => {
    setState((prev) => {
      const next = { ...prev, [key]: value };
      saveState(next);
      return next;
    });
  };
  const saveProfil = (p) => {
    setProfil(p);
    setState((prev) => {
      const next = { ...prev, __profil: p };
      saveState(next);
      return next;
    });
  };

  const current = CHAPTERS[chapterIdx];
  const prev = () => setChapterIdx((i) => Math.max(0, i - 1));
  const next = () => setChapterIdx((i) => Math.min(CHAPTERS.length - 1, i + 1));

  // Progression globale + par chapitre (badges sommaire + CTA fin de chapitre)
  const perChapterProgress = useMemo(() => {
    const map = {};
    CHAPTERS.forEach((c) => {
      map[c.id] = chapterProgress(state, c.id);
    });
    return map;
  }, [state]);

  const currentChapterProgress = perChapterProgress[current.id];
  const isLastChapter = chapterIdx === CHAPTERS.length - 1;
  const showNextChapterCTA =
    currentChapterProgress?.complete &&
    !isLastChapter &&
    !dismissedNext[current.id];

  const progression = useMemo(() => {
    const totals = Object.values(perChapterProgress).reduce(
      (acc, p) => ({ done: acc.done + p.done, total: acc.total + p.total }),
      { done: 0, total: 0 },
    );
    return totals;
  }, [perChapterProgress]);

  return (
    <div className="min-h-screen bg-neutral-950 text-white" data-testid="livret-circulation-book">
      <TrameLivretCross
        current="livret"
        trameTo="/plateau-progression"
        trameLabel="Trame pédagogique Plateau A1/A2"
        livretTo="/livret-circulation"
        livretLabel="Livret Circulation interactif à cocher"
      />
      <SaveProgressCTA done={progression.done} />
      {/* Barre supérieure */}
      <header className="sticky top-0 z-30 bg-neutral-950/95 backdrop-blur border-b border-white/10">
        <div className="max-w-5xl mx-auto px-3 sm:px-4 h-14 flex items-center justify-between gap-1.5 sm:gap-2">
          <div className="flex items-center gap-1.5 sm:gap-2">
            <button
              onClick={() => setMenuOpen(true)}
              className="inline-flex items-center gap-1.5 px-2.5 sm:px-3 h-9 rounded-lg bg-white/[0.05] hover:bg-white/[0.12] border border-white/10 text-white/85 text-[12px] font-bold transition"
              data-testid="btn-menu"
              aria-label="Ouvrir le sommaire"
            >
              <Menu size={14} /> <span className="hidden xs:inline">Sommaire</span><span className="xs:hidden">Menu</span>
            </button>
            <Link
              to="/"
              className="inline-flex items-center justify-center w-9 h-9 sm:w-auto sm:px-3 rounded-lg bg-white/[0.05] hover:bg-white/[0.12] border border-white/10 text-white/85 text-[12px] font-bold transition"
              data-testid="btn-home"
              aria-label="Retour à l'accueil Code2Roues"
            >
              <Home size={14} /> <span className="hidden sm:inline sm:ml-1.5">Accueil</span>
            </Link>
          </div>
          {/* Navigation chapitres — visible en permanence (top sticky) */}
          <div className="flex items-center gap-1" data-testid="top-nav-chapters">
            <button
              onClick={prev}
              disabled={chapterIdx === 0}
              className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-white/[0.05] hover:bg-white/[0.12] disabled:opacity-30 disabled:cursor-not-allowed border border-white/10 text-white/85 transition"
              data-testid="btn-prev-top"
              aria-label="Chapitre précédent"
            >
              <ChevronLeft size={16} />
            </button>
            <div className="hidden sm:block text-[10px] font-black uppercase tracking-widest text-white/40 px-1 tabular-nums">
              {chapterIdx}/{CHAPTERS.length - 1}
            </div>
            <button
              onClick={next}
              disabled={chapterIdx === CHAPTERS.length - 1}
              className="inline-flex items-center justify-center w-9 h-9 rounded-lg text-white shadow-md disabled:opacity-30 disabled:cursor-not-allowed transition"
              style={{ background: chapterIdx === CHAPTERS.length - 1 ? '#333' : current.color }}
              data-testid="btn-next-top"
              aria-label="Chapitre suivant"
            >
              <ChevronRight size={16} />
            </button>
          </div>
          <div className="hidden md:block text-[9.5px] font-black uppercase tracking-[0.22em] text-white/60 truncate min-w-0 flex-1 text-center">
            {current.n && <span className="text-cyan-400">{current.n} · </span>}{current.title}
          </div>
          <div className="flex items-center gap-1.5 sm:gap-2">
            <button
              type="button"
              onClick={() => setQrOpen(true)}
              className="inline-flex items-center justify-center w-9 h-9 sm:w-auto sm:px-3 rounded-lg bg-rose-500/20 hover:bg-rose-500/30 border border-rose-500/40 text-rose-200 text-[12px] font-black uppercase tracking-wider shadow-sm transition"
              data-testid="btn-qr-share"
              aria-label="Partager par QR code"
              title="Partager par QR code (élève sur son téléphone)"
            >
              <QrCode size={13} /> <span className="hidden sm:inline sm:ml-1.5">QR</span>
            </button>
            <Link
              to="/livret-circulation/print"
              className="inline-flex items-center justify-center w-9 h-9 sm:w-auto sm:px-3 rounded-lg bg-cyan-600 hover:bg-cyan-500 text-white text-[12px] font-bold shadow-md transition"
              data-testid="btn-download-pdf"
              aria-label="Télécharger la version PDF imprimable"
            >
              <Download size={13} /> <span className="hidden sm:inline sm:ml-1.5">PDF</span>
            </Link>
            <div className="text-[10px] text-white/50 tabular-nums font-bold hidden sm:block">
              {progression.done}/{progression.total}
            </div>
          </div>
        </div>
        <div className="h-1 bg-white/[0.05]">
          <div
            className="h-full transition-all duration-300"
            style={{
              width: `${((chapterIdx) / (CHAPTERS.length - 1)) * 100}%`,
              background: `linear-gradient(90deg, ${current.color} 0%, #22c55e 100%)`,
            }}
          />
        </div>
        {/* Titre chapitre sur mobile */}
        <div className="md:hidden px-4 py-2 bg-black/40 border-b border-white/5">
          <div className="text-[9.5px] font-black uppercase tracking-[0.2em] text-white/60 truncate">
            {current.n && <span className="text-cyan-400">{current.n} · </span>}{current.title}
          </div>
        </div>
      </header>

      {/* Contenu chapitre */}
      <main className="max-w-3xl mx-auto px-4 py-8 pb-32">
        {current.id === 'cover' && <ChapterCover profil={profil} onSave={saveProfil} />}
        {current.id === 'ch01' && <Chapter01 state={state} setValue={setValue} />}
        {current.id === 'ch02' && <Chapter02 state={state} setValue={setValue} />}
        {current.id === 'ch03' && <Chapter03 state={state} setValue={setValue} />}
        {current.id === 'ch04' && <Chapter04 state={state} setValue={setValue} />}
        {current.id === 'ch05' && <Chapter05 state={state} setValue={setValue} />}
        {current.id === 'ch06' && <Chapter06 state={state} setValue={setValue} />}
        {current.id === 'ch07' && <Chapter07 state={state} setValue={setValue} />}

        {/* CTA « Chapitre complet → passer au suivant » — apparaît
            automatiquement quand tous les items du chapitre sont cochés,
            jusqu'à ce que l'élève l'ait fermé ou soit passé au suivant. */}
        {showNextChapterCTA && (
          <div
            className="mt-8 rounded-3xl border-2 border-emerald-500/60 bg-gradient-to-br from-emerald-500/15 via-emerald-500/5 to-transparent p-5 sm:p-6 relative"
            data-testid="chapter-complete-cta"
          >
            <button
              type="button"
              onClick={() => setDismissedNext((d) => ({ ...d, [current.id]: true }))}
              className="absolute top-3 right-3 w-8 h-8 rounded-full bg-black/30 hover:bg-black/50 border border-white/10 text-white/60 hover:text-white flex items-center justify-center transition"
              aria-label="Fermer la suggestion"
              data-testid="chapter-complete-dismiss"
            >
              <X size={14} />
            </button>
            <div className="flex items-start gap-3 pr-8">
              <div className="w-11 h-11 rounded-xl bg-emerald-500/20 border border-emerald-500/40 flex items-center justify-center flex-shrink-0">
                <CheckCircle2 size={22} className="text-emerald-300" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] font-black uppercase tracking-widest text-emerald-300 mb-0.5">
                  Chapitre {current.n} complet · {currentChapterProgress.done} / {currentChapterProgress.total}
                </div>
                <h3 className="text-white font-black text-lg leading-tight" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
                  Bravo, tout est validé pour ce chapitre !
                </h3>
                <p className="text-white/70 text-[13px] mt-1.5 leading-relaxed">
                  Tu peux passer au chapitre suivant : <strong className="text-white">{CHAPTERS[chapterIdx + 1]?.n} · {CHAPTERS[chapterIdx + 1]?.title}</strong>.
                </p>
                <button
                  type="button"
                  onClick={next}
                  className="mt-3 inline-flex items-center gap-1.5 h-10 px-4 rounded-xl text-white text-[12.5px] font-black uppercase tracking-widest shadow-lg transition hover:opacity-90"
                  style={{ background: CHAPTERS[chapterIdx + 1]?.color || '#10b981' }}
                  data-testid="chapter-complete-next"
                >
                  Chapitre suivant <ChevronRight size={16} />
                </button>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Navigation bas de page */}
      <nav className="fixed bottom-0 left-0 right-0 z-30 bg-neutral-950/95 backdrop-blur border-t border-white/10">
        <div className="max-w-5xl mx-auto px-4 h-16 flex items-center justify-between gap-2">
          <button
            onClick={prev}
            disabled={chapterIdx === 0}
            className="inline-flex items-center gap-1.5 px-4 h-11 rounded-xl bg-white/[0.05] hover:bg-white/[0.12] disabled:opacity-30 disabled:cursor-not-allowed border border-white/10 text-white/85 text-[13px] font-bold transition"
            data-testid="btn-prev"
          >
            <ChevronLeft size={16} /> <span className="hidden sm:inline">Précédent</span>
          </button>
          <div className="flex-1 text-center">
            <div className="text-[10px] uppercase tracking-widest text-white/40 font-bold">Chapitre {chapterIdx}/{CHAPTERS.length - 1}</div>
            <div className="text-[11px] text-white/70 font-semibold truncate">{current.title}</div>
          </div>
          <button
            onClick={next}
            disabled={chapterIdx === CHAPTERS.length - 1}
            className="inline-flex items-center gap-1.5 px-4 h-11 rounded-xl text-white text-[13px] font-bold shadow-lg transition disabled:opacity-30 disabled:cursor-not-allowed"
            style={{ background: current.color }}
            data-testid="btn-next"
          >
            <span className="hidden sm:inline">Suivant</span> <ChevronRight size={16} />
          </button>
        </div>
      </nav>

      {/* Sommaire drawer */}
      {menuOpen && (
        <div className="fixed inset-0 z-40 bg-black/70 backdrop-blur-sm" onClick={() => setMenuOpen(false)}>
          <div
            className="absolute top-0 left-0 h-full w-full max-w-sm bg-neutral-900 border-r border-white/10 shadow-2xl overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-neutral-900 border-b border-white/10 px-5 py-4 flex items-center justify-between">
              <div>
                <div className="text-[9px] font-black uppercase tracking-[0.22em] text-cyan-400">code2roues.fr</div>
                <div className="text-[16px] font-black text-white" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>Sommaire</div>
              </div>
              <button onClick={() => setMenuOpen(false)} className="p-1.5 rounded hover:bg-white/[0.06] text-white/60 hover:text-white" aria-label="Fermer">
                <X size={18} />
              </button>
            </div>
            <div className="p-3 space-y-1">
              {CHAPTERS.map((c, i) => {
                const Icon = c.icon;
                const isCurrent = i === chapterIdx;
                const p = perChapterProgress[c.id] || { done: 0, total: 0, complete: false };
                const hasItems = p.total > 0;
                const isComplete = p.complete;
                const hasStarted = p.done > 0;
                return (
                  <button
                    key={c.id}
                    onClick={() => { setChapterIdx(i); setMenuOpen(false); }}
                    className={`w-full flex items-center gap-3 p-3 rounded-xl transition text-left ${
                      isCurrent ? 'bg-white/[0.10] border border-white/20' : 'hover:bg-white/[0.06] border border-transparent'
                    }`}
                    data-testid={`menu-item-${c.id}`}
                  >
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0 relative" style={{ background: `${c.color}25`, color: c.color }}>
                      <Icon size={18} />
                      {isComplete && (
                        <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-emerald-500 border-2 border-neutral-900 flex items-center justify-center" title="Chapitre complet">
                          <CheckCircle2 size={10} className="text-white" />
                        </span>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[10px] font-black uppercase tracking-widest" style={{ color: c.color }}>
                        {c.n ? `Chapitre ${c.n}` : 'Introduction'}
                      </div>
                      <div className="text-white text-[13px] font-bold leading-tight">{c.title}</div>
                      {/* Badge progression */}
                      {hasItems && (
                        <div className="mt-1.5 flex items-center gap-1.5">
                          <div className="h-1 flex-1 max-w-[80px] rounded-full bg-white/10 overflow-hidden">
                            <div
                              className="h-full transition-all"
                              style={{
                                width: `${Math.round((p.done / p.total) * 100)}%`,
                                background: isComplete ? '#10b981' : c.color,
                              }}
                            />
                          </div>
                          <span
                            className={`text-[10px] font-black tabular-nums ${
                              isComplete ? 'text-emerald-300' : hasStarted ? 'text-white/80' : 'text-white/40'
                            }`}
                            data-testid={`menu-progress-${c.id}`}
                          >
                            {p.done}/{p.total}
                          </span>
                        </div>
                      )}
                    </div>
                    {isCurrent && <div className="w-1.5 h-6 rounded-full" style={{ background: c.color }} />}
                  </button>
                );
              })}
            </div>
            <div className="p-3 border-t border-white/10 mt-2 space-y-2">
              <Link
                to="/cours/premiere-circulation-a2"
                onClick={() => setMenuOpen(false)}
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-gradient-to-r from-red-600 to-red-500 hover:from-red-500 hover:to-red-400 text-white text-[13px] font-black uppercase tracking-wider shadow-lg transition"
                data-testid="drawer-btn-see-full-course"
              >
                <Presentation size={14} /> Cours n°1 · 1ʳᵉ circulation
              </Link>
              <Link
                to="/cours/deuxieme-circulation-a2"
                onClick={() => setMenuOpen(false)}
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-gradient-to-r from-red-600 to-red-500 hover:from-red-500 hover:to-red-400 text-white text-[13px] font-black uppercase tracking-wider shadow-lg transition"
                data-testid="drawer-btn-see-full-course-2"
              >
                <Presentation size={14} /> Cours n°2 · 2ᵉ circulation
              </Link>
              <Link
                to="/cours/troisieme-circulation-a2"
                onClick={() => setMenuOpen(false)}
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-gradient-to-r from-red-600 to-red-500 hover:from-red-500 hover:to-red-400 text-white text-[13px] font-black uppercase tracking-wider shadow-lg transition"
                data-testid="drawer-btn-see-full-course-3"
              >
                <Presentation size={14} /> Cours n°3 · Autonomie
              </Link>
              <Link
                to="/cours/quatrieme-circulation-a2"
                onClick={() => setMenuOpen(false)}
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-gradient-to-r from-red-600 to-red-500 hover:from-red-500 hover:to-red-400 text-white text-[13px] font-black uppercase tracking-wider shadow-lg transition"
                data-testid="drawer-btn-see-full-course-4"
              >
                <Presentation size={14} /> Cours n°4 · Niveau examen
              </Link>
              <Link
                to="/cours/cinquieme-circulation-a2"
                onClick={() => setMenuOpen(false)}
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-gradient-to-r from-red-600 to-red-500 hover:from-red-500 hover:to-red-400 text-white text-[13px] font-black uppercase tracking-wider shadow-lg transition"
                data-testid="drawer-btn-see-full-course-5"
              >
                <Presentation size={14} /> Cours n°5 · Proche examen
              </Link>
              <Link
                to="/cours/sixieme-circulation-a2"
                onClick={() => setMenuOpen(false)}
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-gradient-to-r from-red-600 to-red-500 hover:from-red-500 hover:to-red-400 text-white text-[13px] font-black uppercase tracking-wider shadow-lg transition"
                data-testid="drawer-btn-see-full-course-6"
              >
                <Presentation size={14} /> Cours n°6 · Bilan final
              </Link>
              <Link
                to="/livret-circulation/print"
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-cyan-600 hover:bg-cyan-500 text-white text-[13px] font-bold shadow-lg transition"
                data-testid="drawer-btn-pdf"
              >
                <Download size={14} /> Télécharger le PDF imprimable
              </Link>
              <Link
                to="/livret-plateau"
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-cyan-600/20 hover:bg-cyan-600/30 text-cyan-300 text-[13px] font-bold transition border border-cyan-500/30"
              >
                <Bike size={14} /> Voir aussi le livret plateau
              </Link>
              <div className="text-[10px] text-white/40 text-center mt-2">© Bruno LLORENS · CODE 2 ROUES 2026</div>
            </div>
          </div>
        </div>
      )}

      {/* Modale QR — partage du livret sur smartphone élève */}
      <LivretQrShareModal
        open={qrOpen}
        onClose={() => setQrOpen(false)}
        livretPath="/livret-circulation"
        livretTitle="Livret Circulation A2"
        livretSubtitle="Progression élève · 7 chapitres"
        accentColor="#0ea5e9"
        tabletMode
        bypassAuth
      />
    </div>
  );
}

// ═══════ Sous-composants réutilisables ══════════════════════════════════

function ChapterHeader({ n, color, title, subtitle }) {
  return (
    <div className="mb-6">
      <div className="inline-flex items-center gap-2 text-[10px] font-black uppercase tracking-[0.22em] mb-2" style={{ color }}>
        Chapitre {n}
      </div>
      <h2 className="text-3xl sm:text-4xl font-black leading-tight text-white" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
        {title}
      </h2>
      {subtitle && <p className="text-white/60 mt-2 text-[14px] italic">{subtitle}</p>}
    </div>
  );
}

function ProfilField({ label, value, onChange, onBlur, type = 'text' }) {
  return (
    <label className="block">
      <div className="text-[10px] font-black uppercase tracking-[0.22em] text-white/50 mb-1">{label}</div>
      <input
        type={type}
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        className="w-full bg-white/[0.05] border border-white/10 rounded-lg px-3 h-10 text-white text-[13px] font-semibold focus:border-cyan-400 focus:outline-none"
        placeholder="…"
      />
    </label>
  );
}

function StatusExplain({ label, color, desc }) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.03] p-3 text-center">
      <div className="text-[10px] font-black uppercase tracking-[0.2em] mb-1" style={{ color }}>{label}</div>
      <div className="text-[10.5px] text-white/70 leading-snug">{desc}</div>
    </div>
  );
}

function CheckItem({ label, desc, checked, onToggle, testid }) {
  return (
    <button
      onClick={onToggle}
      className={`w-full flex items-start gap-3 p-3.5 rounded-xl border text-left transition ${
        checked
          ? 'bg-emerald-500/10 border-emerald-500/40'
          : 'bg-white/[0.03] border-white/10 hover:bg-white/[0.06]'
      }`}
      data-testid={testid}
    >
      {checked ? (
        <CheckCircle2 size={20} className="text-emerald-400 shrink-0 mt-0.5" />
      ) : (
        <Circle size={20} className="text-white/30 shrink-0 mt-0.5" />
      )}
      <div className="flex-1 min-w-0">
        <div className={`text-[13px] font-bold ${checked ? 'text-emerald-300' : 'text-white/90'}`}>{label}</div>
        {desc && <div className="text-[11px] text-white/55 mt-0.5 leading-snug">{desc}</div>}
      </div>
    </button>
  );
}

function InfoBox({ color, title, children }) {
  return (
    <div className="rounded-2xl border p-4 mb-4" style={{ borderColor: `${color}40`, background: `${color}10` }}>
      <div className="text-[10px] font-black uppercase tracking-[0.22em] mb-2" style={{ color }}>{title}</div>
      <div className="text-white/80 text-[13px] leading-relaxed space-y-2">{children}</div>
    </div>
  );
}

// ═══════ CHAPITRES ═══════════════════════════════════════════════════════

function ChapterCover({ profil, onSave }) {
  const [form, setForm] = useState(profil);
  useEffect(() => setForm(profil), [profil]);
  return (
    <div className="animate-fadein">
      <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-br from-cyan-600/30 via-blue-500/20 to-transparent p-8 sm:p-12 mb-6">
        <div className="absolute -right-16 -bottom-8 opacity-10 pointer-events-none">
          <Route size={280} strokeWidth={1} />
        </div>
        <div className="relative">
          <div className="inline-flex items-center gap-2 text-[10px] font-black uppercase tracking-[0.28em] text-cyan-400 mb-3">
            <Sparkles size={11} /> Version premium
          </div>
          <div className="text-[10px] font-black uppercase tracking-[0.22em] text-white/60 mb-1">code2roues.fr</div>
          <h1 className="text-[36px] sm:text-[52px] font-black text-white leading-[1.05]" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
            Livret élève<br />
            <span className="text-cyan-400">Circulation A1/A2</span>
          </h1>
          <div className="mt-3 text-[13px] font-black uppercase tracking-[0.28em] text-white/70">
            SORTIR · OBSERVER · ANTICIPER
          </div>
          <p className="mt-4 text-white/70 text-[14px] leading-relaxed max-w-md">
            L'épreuve circulation A2 dure environ <strong className="text-white">40 minutes sur route ouverte</strong>. Coefficient 3 à l'examen final. Ce livret t'accompagne étape par étape.
          </p>

          <div className="mt-6 flex flex-wrap gap-2">
            <Link
              to="/cours/premiere-circulation-a2"
              className="inline-flex items-center gap-2 px-4 h-10 rounded-xl bg-gradient-to-r from-red-600 to-red-500 hover:from-red-500 hover:to-red-400 text-white text-[12px] font-black uppercase tracking-wider shadow-lg shadow-red-500/20 transition-all"
              data-testid="btn-cover-see-full-course"
              aria-label="Voir le cours n°1"
            >
              <Presentation size={14} />
              <span>Cours n°1</span>
            </Link>
            <Link
              to="/cours/deuxieme-circulation-a2"
              className="inline-flex items-center gap-2 px-4 h-10 rounded-xl bg-gradient-to-r from-red-600 to-red-500 hover:from-red-500 hover:to-red-400 text-white text-[12px] font-black uppercase tracking-wider shadow-lg shadow-red-500/20 transition-all"
              data-testid="btn-cover-see-full-course-2"
              aria-label="Voir le cours n°2"
            >
              <Presentation size={14} />
              <span>Cours n°2</span>
            </Link>
            <Link
              to="/cours/troisieme-circulation-a2"
              className="inline-flex items-center gap-2 px-4 h-10 rounded-xl bg-gradient-to-r from-red-600 to-red-500 hover:from-red-500 hover:to-red-400 text-white text-[12px] font-black uppercase tracking-wider shadow-lg shadow-red-500/20 transition-all"
              data-testid="btn-cover-see-full-course-3"
              aria-label="Voir le cours n°3"
            >
              <Presentation size={14} />
              <span>Cours n°3</span>
            </Link>
            <Link
              to="/cours/quatrieme-circulation-a2"
              className="inline-flex items-center gap-2 px-4 h-10 rounded-xl bg-gradient-to-r from-red-600 to-red-500 hover:from-red-500 hover:to-red-400 text-white text-[12px] font-black uppercase tracking-wider shadow-lg shadow-red-500/20 transition-all"
              data-testid="btn-cover-see-full-course-4"
              aria-label="Voir le cours n°4"
            >
              <Presentation size={14} />
              <span>Cours n°4</span>
            </Link>
            <Link
              to="/cours/cinquieme-circulation-a2"
              className="inline-flex items-center gap-2 px-4 h-10 rounded-xl bg-gradient-to-r from-red-600 to-red-500 hover:from-red-500 hover:to-red-400 text-white text-[12px] font-black uppercase tracking-wider shadow-lg shadow-red-500/20 transition-all"
              data-testid="btn-cover-see-full-course-5"
              aria-label="Voir le cours n°5"
            >
              <Presentation size={14} />
              <span>Cours n°5</span>
            </Link>
            <Link
              to="/cours/sixieme-circulation-a2"
              className="inline-flex items-center gap-2 px-4 h-10 rounded-xl bg-gradient-to-r from-red-600 to-red-500 hover:from-red-500 hover:to-red-400 text-white text-[12px] font-black uppercase tracking-wider shadow-lg shadow-red-500/20 transition-all"
              data-testid="btn-cover-see-full-course-6"
              aria-label="Voir le cours n°6"
            >
              <Presentation size={14} />
              <span>Cours n°6</span>
              <span className="ml-1 px-1.5 py-0.5 text-[9px] rounded bg-white/20">FINAL</span>
            </Link>
          </div>
          <p className="mt-2 text-[11px] text-white/50 italic">
            6 cours premium — 84 chapitres pédagogiques · 12 h minimales couvertes
          </p>
        </div>
      </div>

      <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5">
        <div className="text-[10px] font-black uppercase tracking-[0.22em] text-white/50 mb-3">Ce livret appartient à</div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <ProfilField label="Élève" value={form.eleve} onChange={(v) => setForm((f) => ({ ...f, eleve: v }))} onBlur={() => onSave(form)} />
          <ProfilField label="Moniteur / auto-école" value={form.moniteur} onChange={(v) => setForm((f) => ({ ...f, moniteur: v }))} onBlur={() => onSave(form)} />
          <ProfilField label="Début formation circu" value={form.debut} onChange={(v) => setForm((f) => ({ ...f, debut: v }))} onBlur={() => onSave(form)} type="date" />
        </div>
      </div>

      <div className="mt-4 text-center text-[11px] text-white/40 italic">
        Support pédagogique CODE 2 ROUES · Complément au plateau · © Bruno LLORENS · 2026
      </div>
    </div>
  );
}

function Chapter01({ state, setValue }) {
  const reperes = [
    { key: 'c-regard',      icon: Eye,     color: '#0ea5e9', title: 'REGARD LOIN',     desc: 'Anticipe 12s à venir, pas ton pneu' },
    { key: 'c-douceur',     icon: Feather, color: '#22c55e', title: 'DOUCEUR',         desc: 'Accélération et freinage progressifs' },
    { key: 'c-equilibre',   icon: Scale,   color: '#eab308', title: 'PLACEMENT',       desc: '1/3 droit hors agglo · milieu en ville' },
    { key: 'c-regularite',  icon: Repeat,  color: '#a855f7', title: 'RÉGULARITÉ',      desc: 'Une vitesse adaptée, sans à-coup' },
  ];
  return (
    <div className="animate-fadein">
      <ChapterHeader n="01" color="#22c55e" title="Bienvenue en circulation" subtitle="Du plateau à la route réelle" />

      <InfoBox color="#22c55e" title="Ce qui change vs. le plateau">
        <p>Sur le plateau, tu maîtrises ta moto <strong>seule</strong> sur un terrain fermé. En circulation, tu partages la route avec <strong>voitures, camions, piétons, cyclistes</strong> — chacun avec ses propres priorités, imprévus et angles morts.</p>
        <p className="text-[12px] italic">L'épreuve circulation dure ~40 min. Coefficient 3 à la note finale. Sanction principale : erreur de comportement compromettant la sécurité.</p>
      </InfoBox>

      <div className="text-[10px] font-black uppercase tracking-[0.22em] text-white/50 mb-3">Comment utiliser ce livret</div>
      <div className="grid grid-cols-3 gap-2 mb-6">
        <StatusExplain label="Découvert" color="#94a3b8" desc="Vu en cours" />
        <StatusExplain label="En cours" color="#f59e0b" desc="Je stabilise" />
        <StatusExplain label="Acquis" color="#10b981" desc="Moniteur valide" />
      </div>

      <div className="text-[10px] font-black uppercase tracking-[0.22em] text-white/50 mb-3">4 repères mentaux à emporter</div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {reperes.map((r) => (
          <CheckItem
            key={r.key}
            label={r.title}
            desc={r.desc}
            checked={!!state[r.key]}
            onToggle={() => setValue(r.key, !state[r.key])}
            testid={`ch01-${r.key}`}
          />
        ))}
      </div>
    </div>
  );
}

function Chapter02({ state, setValue }) {
  const epi = [
    { key: 'c02-casque',   label: 'Casque homologué E22-06', desc: 'Attaché correctement, jugulaire tendue' },
    { key: 'c02-gants',    label: 'Gants CE homologués moto', desc: 'Étiquette avec picto moto obligatoire' },
    { key: 'c02-blouson',  label: 'Blouson manches longues', desc: 'Renforts coques épaules & coudes idéalement' },
    { key: 'c02-pantalon', label: 'Pantalon long solide',    desc: 'Jean renforcé ou pantalon moto' },
    { key: 'c02-bottes',   label: 'Chaussures montantes',    desc: 'Cheville protégée, semelle antidérapante' },
    { key: 'c02-gilet',    label: 'Gilet haute visibilité',  desc: 'Obligatoire à bord (art. R431-1-2)' },
  ];
  const verifs = [
    { key: 'c02-huile',    label: 'Niveau d\'huile',          desc: 'À vérifier tous les 500 km' },
    { key: 'c02-liquide',  label: 'Liquide de frein',         desc: 'Niveau + couleur (jaunâtre si vieilli)' },
    { key: 'c02-pneus',    label: 'Pneus (usure + pression)', desc: 'Sculpture > 1 mm · pression selon manuel' },
    { key: 'c02-lumieres', label: 'Feux + clignotants',       desc: 'Croisement, route, stop, warnings, clignos' },
    { key: 'c02-chaine',   label: 'Chaîne de transmission',   desc: 'Tension + graissage réguliers' },
  ];
  return (
    <div className="animate-fadein">
      <ChapterHeader n="02" color="#eab308" title="Prise en main avant départ" subtitle="EPI complet + vérifications techniques" />

      <InfoBox color="#eab308" title="Rappel officiel — EPI obligatoire">
        <p>Depuis novembre 2016 : <strong>casque homologué E22-06</strong> + <strong>gants CE moto</strong> obligatoires. Le reste (blouson, pantalon, bottes, gilet) est fortement recommandé — un examinateur le vérifiera visuellement.</p>
      </InfoBox>

      <div className="text-[10px] font-black uppercase tracking-[0.22em] text-white/50 mb-3">🦺 Équipement de protection individuelle</div>
      <div className="space-y-2 mb-6">
        {epi.map((item) => (
          <CheckItem
            key={item.key}
            label={item.label}
            desc={item.desc}
            checked={!!state[item.key]}
            onToggle={() => setValue(item.key, !state[item.key])}
            testid={`ch02-${item.key}`}
          />
        ))}
      </div>

      <div className="text-[10px] font-black uppercase tracking-[0.22em] text-white/50 mb-3">🔧 Vérifications techniques rapides</div>
      <div className="space-y-2">
        {verifs.map((item) => (
          <CheckItem
            key={item.key}
            label={item.label}
            desc={item.desc}
            checked={!!state[item.key]}
            onToggle={() => setValue(item.key, !state[item.key])}
            testid={`ch02-${item.key}`}
          />
        ))}
      </div>
    </div>
  );
}

function Chapter03({ state, setValue }) {
  const items = [
    { key: 'c03-regard-loin',     label: 'Regard porté LOIN',              desc: '10-15 secondes en avant · pas ton pneu' },
    { key: 'c03-regard-mobile',   label: 'Balayage régulier',              desc: 'Panneaux, rétros tous les 5-8 sec' },
    { key: 'c03-allure',          label: 'Allure adaptée aux limitations', desc: '50/80/90/110/130 selon le contexte' },
    { key: 'c03-position-hors',   label: '1/3 droit hors agglomération',   desc: 'Se ménage marge & visibilité' },
    { key: 'c03-position-ville',  label: 'Milieu de voie en agglomération', desc: 'Empêche voitures de te doubler dans ta voie' },
    { key: 'c03-anticipation',    label: 'Anticipation feux & carrefours', desc: 'Décale ton allure avant d\'y arriver' },
    { key: 'c03-mains-libres',    label: 'Mains détendues sur guidon',     desc: 'Coudes libres, pas contractés' },
  ];
  return (
    <div className="animate-fadein">
      <ChapterHeader n="03" color="#f97316" title="Regard · Allure · Position" subtitle="Les 3 piliers de la conduite en circulation" />

      <InfoBox color="#f97316" title="La règle des 3 principes">
        <p><strong>REGARD :</strong> il conditionne TOUT le reste. Tu vas où tu regardes.</p>
        <p><strong>ALLURE :</strong> jamais trop vite pour ce que tu vois, jamais trop lent pour la circulation autour.</p>
        <p><strong>POSITION :</strong> défensive — assez à droite pour t'éloigner du frontal, assez au centre pour ne pas te faire doubler dans ta voie.</p>
      </InfoBox>

      <div className="text-[10px] font-black uppercase tracking-[0.22em] text-white/50 mb-3">✅ 7 gestes à valider avec ton moniteur</div>
      <div className="space-y-2">
        {items.map((item) => (
          <CheckItem
            key={item.key}
            label={item.label}
            desc={item.desc}
            checked={!!state[item.key]}
            onToggle={() => setValue(item.key, !state[item.key])}
            testid={`ch03-${item.key}`}
          />
        ))}
      </div>
    </div>
  );
}

function Chapter04({ state, setValue }) {
  const items = [
    { key: 'c04-freine-avant', label: 'Je freine AVANT le virage',        desc: 'Tout le freinage en ligne droite' },
    { key: 'c04-retrograde',   label: 'Je passe au bon rapport avant',    desc: 'Vitesse et régime prêts pour la sortie' },
    { key: 'c04-place-large',  label: 'Je m\'élargis (avec discernement)', desc: 'Sans jamais frôler la ligne médiane' },
    { key: 'c04-point-corde',  label: 'Point de corde RETARDÉ',           desc: 'Je vois la sortie avant de pencher' },
    { key: 'c04-regard-sortie',label: 'Regard verrouillé sur la sortie',  desc: 'La moto suit toujours le regard' },
    { key: 'c04-relance',      label: 'Relance douce en sortie',          desc: 'Progressive, moto redressée' },
    { key: 'c04-marge-median', label: '50 cm à 1 m de la ligne médiane',  desc: 'JAMAIS proche du frontal' },
  ];
  return (
    <div className="animate-fadein">
      <ChapterHeader n="04" color="#06b6d4" title="Trajectoire de sécurité en virage" subtitle="Méthode Élargir · Retarder · Freiner avant" />

      <InfoBox color="#06b6d4" title="La règle absolue avant tout">
        <p>En France, on roule sur la <strong>partie droite</strong> (art. R412-9). La trajectoire de sécurité <strong>ADAPTE</strong> cette règle au virage, elle ne la contredit JAMAIS.</p>
        <p className="text-[12px]"><strong>Virage à gauche :</strong> tu restes serré à DROITE (loin du frontal). <strong>Virage à droite :</strong> tu peux t'élargir modérément mais sans frôler l'axe.</p>
      </InfoBox>

      <div className="text-[10px] font-black uppercase tracking-[0.22em] text-white/50 mb-3">🏍️ Check-list virage sécurisé</div>
      <div className="space-y-2 mb-4">
        {items.map((item) => (
          <CheckItem
            key={item.key}
            label={item.label}
            desc={item.desc}
            checked={!!state[item.key]}
            onToggle={() => setValue(item.key, !state[item.key])}
            testid={`ch04-${item.key}`}
          />
        ))}
      </div>

      <div className="rounded-2xl border border-cyan-500/30 bg-cyan-500/5 p-4 text-center">
        <p className="text-cyan-300 text-[13px] font-semibold mb-2">📺 Vidéo pédagogique complète</p>
        <Link to="/magazine/trajectoire-securite-virage" className="inline-flex items-center gap-2 px-4 h-10 rounded-lg bg-cyan-600 hover:bg-cyan-500 text-white text-[12px] font-bold transition">
          <Route size={14} /> Lire le dossier magazine + vidéo
        </Link>
      </div>
    </div>
  );
}

function Chapter05({ state, setValue }) {
  const usagers = [
    { key: 'c05-croisement',  icon: Signpost, color: '#0ea5e9', label: 'Croisement voiture', desc: '50 cm à 1 m de marge — jamais coller la ligne' },
    { key: 'c05-depassement', icon: Zap,      color: '#f97316', label: 'Dépassement d\'un vélo/scooter', desc: 'Marge latérale > 1 m (art. R414-4)' },
    { key: 'c05-camion',      icon: Truck,    color: '#eab308', label: 'Cohabitation camion / bus', desc: 'Angle mort massif — ne JAMAIS rester à leur droite' },
    { key: 'c05-angle-mort',  icon: AlertCircle, color: '#ef4444', label: 'Angle mort auto', desc: 'Passe rapidement, ou reste bien derrière' },
    { key: 'c05-piste',       icon: Bike,     color: '#22c55e', label: 'Traversée piste cyclable', desc: 'Attends que le cycliste soit passé' },
    { key: 'c05-priorite-dr', label: 'Priorité à droite en ville', icon: MapPin, color: '#a855f7', desc: 'Ralentis SYSTÉMATIQUEMENT à toute intersection non signalée' },
    { key: 'c05-passage-p',   label: 'Passage piéton', icon: Users, color: '#ec4899', desc: 'Cède le passage AUSSI aux piétons qui s\'apprêtent à traverser (loi 2018)' },
  ];
  return (
    <div className="animate-fadein">
      <ChapterHeader n="05" color="#6366f1" title="Cohabiter avec les autres usagers" subtitle="Croisements · Dépassements · Priorités" />

      <InfoBox color="#6366f1" title="Le motard = usager vulnérable">
        <p>Les <strong>2RM représentent ~22 % des tués</strong> pour seulement 2 % du trafic. La plupart des accidents en circulation impliquent un <strong>autre usager qui n'a pas vu la moto</strong>.</p>
        <p>Ton meilleur atout : <strong>anticiper les erreurs des autres</strong> avant qu'elles n'arrivent.</p>
      </InfoBox>

      <div className="text-[10px] font-black uppercase tracking-[0.22em] text-white/50 mb-3">🛡️ Situations à maîtriser</div>
      <div className="space-y-2">
        {usagers.map((u) => (
          <CheckItem
            key={u.key}
            label={u.label}
            desc={u.desc}
            checked={!!state[u.key]}
            onToggle={() => setValue(u.key, !state[u.key])}
            testid={`ch05-${u.key}`}
          />
        ))}
      </div>
    </div>
  );
}

function Chapter06({ state, setValue }) {
  const items = [
    { key: 'c06-freinage-avant', label: 'Frein AVANT : dosage progressif',   desc: '70 % de la puissance de freinage · à privilégier ligne droite' },
    { key: 'c06-freinage-arr',   label: 'Frein ARRIÈRE : stabilité',         desc: 'Aide à l\'équilibre · complète le frein avant' },
    { key: 'c06-abs',            label: 'Rôle de l\'ABS',                    desc: 'Empêche blocage roues (obligatoire depuis 2016 sur A2)' },
    { key: 'c06-eclairage',      label: 'Éclairage : allumé en permanence',  desc: 'Feux de croisement obligatoires jour et nuit (art. R416-1)' },
    { key: 'c06-vent',           label: 'Vent de travers / rafales',         desc: 'Souple sur guidon · ralentir en zone exposée' },
    { key: 'c06-pluie',          label: 'Pluie / route mouillée',            desc: 'Distances × 2 · évite marquages au sol' },
    { key: 'c06-nuit',           label: 'Conduite de nuit',                  desc: 'Gilet rétro · vitesse réduite · distances triplées' },
  ];
  return (
    <div className="animate-fadein">
      <ChapterHeader n="06" color="#a855f7" title="Vérifications sécurité" subtitle="Question orale · avant l'épreuve en circulation" />

      <InfoBox color="#a855f7" title="La question orale (2 min de plus)">
        <p>Avant de partir en circulation, l'inspecteur peut te poser une <strong>question sur la sécurité active/passive</strong> : freinage, éclairage, vent, pluie, chargement, transport passager, groupe...</p>
        <p>Notation : jusqu'à <strong>3 points bonus/malus</strong> qui peuvent basculer une épreuve limite.</p>
      </InfoBox>

      <div className="text-[10px] font-black uppercase tracking-[0.22em] text-white/50 mb-3">🎯 Notions à réviser AVANT l'épreuve</div>
      <div className="space-y-2">
        {items.map((item) => (
          <CheckItem
            key={item.key}
            label={item.label}
            desc={item.desc}
            checked={!!state[item.key]}
            onToggle={() => setValue(item.key, !state[item.key])}
            testid={`ch06-${item.key}`}
          />
        ))}
      </div>
    </div>
  );
}

function Chapter07({ state, setValue }) {
  const items = [
    { key: 'c07-plateau-ok',   label: 'Plateau validé (6 manœuvres)',      desc: 'Prérequis absolu' },
    { key: 'c07-etm-ok',       label: 'Code moto (ETM) obtenu',            desc: 'Depuis moins de 5 ans' },
    { key: 'c07-h20-circu',    label: '20h de circulation minimum',        desc: 'Volume moyen pour se sentir à l\'aise' },
    { key: 'c07-milieu-varies', label: 'J\'ai roulé en 4 milieux', desc: 'Ville, hors agglo, autoroute, nuit ou pluie' },
    { key: 'c07-question-ok',  label: 'Question orale préparée',           desc: 'Chapitre 6 révisé' },
    { key: 'c07-moniteur-go',  label: 'Mon moniteur me valide GO !',       desc: 'La certitude vaut mieux que le doute' },
    { key: 'c07-eqp-complet',  label: 'EPI complet vérifié le jour J',     desc: 'Casque, gants, blouson, gilet, papiers' },
  ];
  return (
    <div className="animate-fadein">
      <ChapterHeader n="07" color="#ec4899" title="Prêt pour l'examen ?" subtitle="Check-list finale avant l'épreuve" />

      <InfoBox color="#ec4899" title="Le jour J">
        <p>L'épreuve dure <strong>~40 minutes sur route ouverte</strong>. Tu es équipé d'un casque radio pour recevoir les consignes de l'inspecteur qui suit en voiture derrière toi.</p>
        <p><strong>Notation :</strong> A = Favorable · B = Insuffisant · C = Défavorable. Coefficient 3 sur le résultat final.</p>
      </InfoBox>

      <div className="text-[10px] font-black uppercase tracking-[0.22em] text-white/50 mb-3">🏁 Check-list du candidat prêt</div>
      <div className="space-y-2 mb-6">
        {items.map((item) => (
          <CheckItem
            key={item.key}
            label={item.label}
            desc={item.desc}
            checked={!!state[item.key]}
            onToggle={() => setValue(item.key, !state[item.key])}
            testid={`ch07-${item.key}`}
          />
        ))}
      </div>

      <div className="rounded-3xl border border-emerald-500/30 bg-gradient-to-br from-emerald-500/15 to-cyan-500/10 p-6 text-center">
        <Trophy className="w-12 h-12 text-emerald-400 mx-auto mb-3" />
        <h3 className="text-2xl font-black text-white mb-2" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
          Tu es prêt(e).
        </h3>
        <p className="text-emerald-200 text-[13px] leading-relaxed max-w-md mx-auto">
          Fais confiance à ton moniteur, à ton apprentissage, et à tes automatismes construits pendant des dizaines d'heures. <strong>Roule ta chance !</strong>
        </p>
        <Link to="/magazine" className="inline-flex items-center gap-2 mt-4 px-4 h-10 rounded-lg bg-white/10 hover:bg-white/15 text-white text-[12px] font-bold transition">
          <Sparkles size={14} /> Continue à te documenter
        </Link>
      </div>
    </div>
  );
}
